<div
  class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 py-5 border-b border-slate-100"
>
  <h2 class="text-base font-semibold text-slate-800">User Management</h2>
  <div class="flex items-center gap-3 flex-1 sm:flex-none sm:justify-end">
    <div class="relative flex-1 sm:w-72">
      <svg
        class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <button
        type="button"
        (click)="openFilters()"
        class="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-left text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all duration-200"
      >
        {{
          activeFilterCount
            ? activeFilterCount +
              " active filter" +
              (activeFilterCount > 1 ? "s" : "")
            : "Search and filter users"
        }}
      </button>
    </div>

    <button
      (click)="openCreateModal()"
      class="flex items-center gap-2 px-4 py-2 page-btn page-btn-primary active:scale-95 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 4v16m8-8H4"
        />
      </svg>
      Create User
    </button>
  </div>
</div>

<div class="overflow-x-auto">
  <table class="w-full text-sm">
    <thead>
      <tr class="bg-slate-50/70">
        <th
          *ngFor="let col of columns"
          class="px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap select-none"
          [class.text-left]="col === 'Users'"
          [class.text-center]="col !== 'Users'"
        >
          {{ col }}
        </th>
      </tr>
    </thead>
    <tbody class="divide-y divide-slate-100">
      @if (isLoadingUsers) {
        <tr>
          <td colspan="7" class="px-6 py-16 text-center">
            <div class="flex flex-col items-center gap-3">
              <div
                class="size-10 animate-spin rounded-full border-4 border-slate-200 border-t-vms-bg-secondary"
              ></div>
              <p class="text-sm font-medium text-slate-500">Loading users...</p>
            </div>
          </td>
        </tr>
      } @else {
        @for (user of users; track $index; let i = $index) {
          <tr
            class="relative group hover:bg-teal-50/30 transition-colors duration-150 animate-row-in"
            [style.animation-delay]="i * 60 + 'ms'"
            [style.z-index]="users.length - i"
          >
            <td class="px-6 py-4 whitespace-nowrap text-left">
              <div class="font-medium text-slate-800">{{ user.full_name }}</div>
              
              @if (user.email) {
                <div class="mt-1 text-slate-400">{{ user.email }}</div>
              }

              @if (user.phone_number) {
                <div class="mt-1 text-slate-400">{{ user.phone_number }}</div>
              }
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <span
                [class]="getAccountTypeClass(user.account_type)"
                class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
              >
                {{ user.account_type }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-center">
              @if(!user.roles || user.roles.length === 0) {
                <span
                  class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-sky-100 text-sky-700"
                >
                0
                </span>
              } @else {
                @if (user.roles.length > 1) {
                  <app-dropdown-menu>
                    <span
                      trigger
                      class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-sky-100 text-sky-700 cursor-pointer"
                    >
                      {{user.roles.length}}
                    </span>
        
                    <ng-container content>
                      @for (role of user.roles; track $index) {
                        <p class="p-2 border-b last:border-b-0 text-center text-sm">
                          {{ role.name }}
                        </p>
                      }
                    </ng-container>
                  </app-dropdown-menu>
                } @else {
                  <span
                    class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-sky-100 text-sky-700"
                  >
                    {{ user.roles[0].name }}
                  </span>
                }
              }
            </td>
            <td class="px-6 py-4 text-center text-slate-600 whitespace-nowrap">
              {{ user?.asin?.trim() || "N/A" }}
            </td>
            <td class="px-6 py-4 text-center text-slate-600 whitespace-nowrap">
              {{ user?.mda_name?.trim() || "N/A" }}
            </td>
            <td class="px-6 py-4 text-slate-500 whitespace-nowrap text-center">
              {{ user.created_at | date }}
            </td>
            <td class="px-6 py-4 text-center">
              <span
                [class]="getStatusClass(user.account_status)"
                class="px-2.5 py-1 rounded-full text-xs font-medium"
              >
                {{ user.account_status }}
              </span>
            </td>
            <td class="px-6 py-4 text-center">
              <app-dropdown-menu>
                <button
                  class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition-all duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  aria-label="Open actions"
                  title="Actions"
                  trigger
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="5" cy="12" r="1.5" />
                    <circle cx="12" cy="12" r="1.5" />
                    <circle cx="19" cy="12" r="1.5" />
                  </svg>
                </button>
    
                <ng-container content>
                  <button
                    (click)="onEdit(user)"
                    class="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3.5 w-3.5 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                  <button
                    (click)="onToggleStatus(user)"
                    class="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3.5 w-3.5 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                      />
                    </svg>
                    {{
                      user.account_status === "ACTIVE" ? "Deactivate" : "Activate"
                    }}
                  </button>
                  <hr class="my-1 border-slate-100" />
                  @if (user.account_status !== "DELETED") {
                    <button
                      (click)="onDeleteUser(user.id)"
                      class="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  }
                </ng-container>
              </app-dropdown-menu>
            </td>
          </tr>
        } @empty {
          <tr>
            <td colspan="6" class="px-6 py-16 text-center">
              <div class="flex flex-col items-center gap-3">
                <div
                  class="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p class="text-sm font-medium text-slate-500">No users found</p>
                <p class="text-xs text-slate-400">
                  Try adjusting your search or create a new user
                </p>
              </div>
            </td>
          </tr>
        }
      }


    </tbody>
  </table>
</div>

<div
  class="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50"
>
  <p class="text-xs text-slate-400">
    Showing {{ users.length }} of {{ totalItems }} users
  </p>
  <div class="flex items-center gap-1">
    <button
      (click)="goToPreviousPage()"
      [disabled]="!hasPreviousPage || isLoadingUsers"
      class="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors duration-150 disabled:opacity-40"
    >
      Previous
    </button>
    <button
      class="px-3 py-1.5 text-xs bg-vms-bg-secondary text-vms-text-secondary rounded-md shadow-sm"
    >
      {{ currentPage }}
    </button>
    <button
      (click)="goToNextPage()"
      [disabled]="!hasNextPage || isLoadingUsers"
      class="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors duration-150"
    >
      Next
    </button>
  </div>
</div>

@if (isCreateModalOpen) {
  <app-admin-creation-modal
    [isSubmitting]="isSubmitting"
    (close)="closeCreateModal()"
    (createUser)="onCreateUser($event)"
    (updateUser)="onUpdateUser($event)"
    [mode]="mode"
    [user]="targetUser"
  ></app-admin-creation-modal>
}

<app-filter-drawer
  [isOpen]="isFilterDrawerOpen"
  title="Search Users"
  subtitle="Filter the user list by identity, contact details, MDA, or date range."
  submitLabel="Apply"
  resetLabel="Clear"
  [fields]="userFilterFields"
  [initialValue]="userFilters"
  (close)="closeFilters()"
  (apply)="applyFilters($event)"
></app-filter-drawer>
